/**
 * Attaches Mega menu config templates (from nested theme blocks) to matching
 * Main Menu items. Nested theme block settings are not readable from the parent
 * via block.blocks, so each config renders itself inside a <template>.
 */
(function () {
  const HANDLEIZE_RE = /[^a-z0-9]+/g;

  /**
   * @param {string} value
   * @returns {string}
   */
  function handleize(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(HANDLEIZE_RE, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * @param {string} raw
   * @returns {string[]}
   */
  function parseHandles(raw) {
    return String(raw || '')
      .replace(/،/g, ',')
      .replace(/;/g, ',')
      .split(',')
      .map((token) => token.trim())
      .filter(Boolean)
      .flatMap((token) => {
        const down = token.toLowerCase();
        const handle = handleize(token);
        const parts = token.split('/').map((part) => handleize(part)).filter(Boolean);
        return [...new Set([down, handle, ...parts])];
      });
  }

  /**
   * @param {HTMLElement} el
   * @param {string[]} tokens
   * @returns {boolean}
   */
  function elementMatchesHandles(el, tokens) {
    const candidates = [
      el.dataset.pjMenuHandle,
      el.dataset.pjMenuTitleHandle,
      el.dataset.pjMenuTitle,
      handleize(el.dataset.pjMenuHandle),
      handleize(el.dataset.pjMenuTitleHandle),
      handleize(el.dataset.pjMenuTitle),
    ].filter(Boolean);

    return tokens.some((token) => candidates.includes(token) || candidates.includes(handleize(token)));
  }

  /**
   * @param {ParentNode} scope
   * @param {ParentNode} configsRoot
   */
  function attachDesktop(scope, configsRoot) {
    const configs = configsRoot.querySelectorAll('template.pj-mega-menu-config');
    if (!configs.length) return;

    const featuredTpl =
      configsRoot.parentElement?.querySelector('template[data-pj-mega-featured-desktop]') ||
      document.querySelector('template[data-pj-mega-featured-desktop]');
    const menuItems = scope.querySelectorAll(
      '.menu-list__list-item[data-pj-menu-handle], .menu-list__list-item[data-pj-menu-title-handle]'
    );

    configs.forEach((tpl) => {
      const tokens = parseHandles(tpl.dataset.pjMegaHandles);
      if (!tokens.length) return;

      menuItems.forEach((listItem) => {
        if (!(listItem instanceof HTMLElement)) return;
        if (listItem.classList.contains('menu-list__list-item--pj-mega')) {
          listItem.dataset.pjMegaAttached = 'true';
          return;
        }
        if (listItem.dataset.pjMegaAttached === 'true') return;
        if (!elementMatchesHandles(listItem, tokens)) return;

        const desktopSource = tpl.content.querySelector('[data-pj-mega-desktop]');
        // Desktop is attached in Liquid; templates may only carry drawer columns.
        if (!desktopSource) return;

        let submenu = listItem.querySelector(':scope > .menu-list__submenu');
        const link = listItem.querySelector(':scope > [ref="menuitem"], :scope > .menu-list__link');

        if (!submenu) {
          submenu = document.createElement('div');
          submenu.className = 'menu-list__submenu';
          submenu.setAttribute('ref', 'submenu[]');

          const inner = document.createElement('div');
          inner.className = 'menu-list__submenu-inner menu-list__submenu-inner--pj-mega';
          const id = `submenu-pj-${Math.random().toString(36).slice(2, 9)}`;
          inner.id = id;
          submenu.appendChild(inner);
          listItem.appendChild(submenu);

          if (link instanceof HTMLElement) {
            link.setAttribute('aria-haspopup', 'true');
            link.setAttribute('aria-expanded', 'false');
            link.setAttribute('aria-controls', id);
          }
        }

        const inner = submenu.querySelector('.menu-list__submenu-inner') || submenu;
        inner.classList.add('menu-list__submenu-inner--pj-mega');
        inner.replaceChildren();

        const mega = /** @type {HTMLElement} */ (desktopSource.cloneNode(true));
        const featuredSlot = mega.querySelector('[data-pj-mega-featured-slot]');
        if (featuredSlot && featuredTpl) {
          featuredSlot.replaceWith(featuredTpl.content.cloneNode(true));
        }

        while (mega.firstChild) {
          inner.appendChild(mega.firstChild);
        }

        listItem.classList.add('menu-list__list-item--pj-mega');
        listItem.dataset.pjMegaAttached = 'true';
      });
    });
  }

  /**
   * @param {ParentNode} drawerRoot
   * @param {ParentNode} configsRoot
   */
  function attachDrawer(drawerRoot, configsRoot) {
    const configs = configsRoot.querySelectorAll(
      'template.pj-mega-menu-config, [data-pj-mega-drawer-columns][data-pj-mega-handles]'
    );
    if (!configs.length) return;

    const featuredTpl =
      configsRoot.parentElement?.querySelector('template[data-pj-mega-featured-drawer]') ||
      document.querySelector('template[data-pj-mega-featured-drawer]');
    const drawerItems = drawerRoot.querySelectorAll(
      '.menu-drawer__menu > li[data-pj-menu-handle], .menu-drawer__menu > li[data-pj-menu-title-handle]'
    );

    configs.forEach((tpl) => {
      const tokens = parseHandles(tpl.dataset.pjMegaHandles);
      if (!tokens.length) return;

      drawerItems.forEach((listItem, index) => {
        if (!(listItem instanceof HTMLElement)) return;
        if (listItem.dataset.pjMegaAttached === 'true') return;
        if (!elementMatchesHandles(listItem, tokens)) return;

        const columnsSource =
          tpl instanceof HTMLTemplateElement
            ? tpl.content.querySelector('[data-pj-mega-drawer-columns]')
            : tpl.matches('[data-pj-mega-drawer-columns]')
              ? tpl
              : tpl.querySelector('[data-pj-mega-drawer-columns]');
        if (!columnsSource) return;

        const linkEl =
          listItem.querySelector('a.menu-drawer__menu-item, summary.menu-drawer__menu-item, a') ||
          listItem.querySelector('a, summary');
        const title =
          linkEl?.querySelector('.menu-drawer__menu-item-text')?.textContent?.trim() ||
          linkEl?.textContent?.trim() ||
          'Menu';
        const handle = listItem.dataset.pjMenuHandle || handleize(title);
        const animationIndex = listItem.style.getPropertyValue('--menu-drawer-animation-index') || String(index + 1);

        const replacement = document.createElement('li');
        replacement.className = 'menu-drawer__list-item pj-mega-menu-drawer-item';
        replacement.style.setProperty('--menu-drawer-animation-index', animationIndex);
        replacement.dataset.pjMenuHandle = listItem.dataset.pjMenuHandle || '';
        replacement.dataset.pjMenuTitleHandle = listItem.dataset.pjMenuTitleHandle || '';
        replacement.dataset.pjMenuTitle = listItem.dataset.pjMenuTitle || '';
        replacement.dataset.pjMegaAttached = 'true';

        const details = document.createElement('details');
        details.id = `Details-menu-drawer-menu-item-${handle}`;
        details.className = 'menu-drawer__menu-container';

        const summary = document.createElement('summary');
        summary.id = `HeaderDrawer-${handle}`;
        summary.className =
          'menu-drawer__menu-item menu-drawer__menu-item--mainlist menu-drawer__animated-element focus-inset';
        summary.setAttribute('on:click', 'header-drawer/open/submenu');

        const titleSpan = document.createElement('span');
        titleSpan.className = 'menu-drawer__menu-item-text wrap-text';
        titleSpan.textContent = title;
        summary.appendChild(titleSpan);

        const existingCaret = listItem.querySelector('.icon-caret--forward, .svg-wrapper.icon-caret');
        if (existingCaret) {
          summary.appendChild(existingCaret.cloneNode(true));
        } else {
          const caret = document.createElement('span');
          caret.className = 'svg-wrapper icon-caret icon-caret--forward';
          summary.appendChild(caret);
        }

        const submenu = document.createElement('div');
        submenu.id = `link-${handle}`;
        submenu.className = 'menu-drawer__submenu has-submenu pj-mega-menu-drawer';
        submenu.tabIndex = -1;
        submenu.style.setProperty('--menu-drawer-animation-index', animationIndex);

        const inner = document.createElement('div');
        inner.className = 'menu-drawer__inner-submenu';

        const navButtons = document.createElement('div');
        navButtons.className = 'menu-drawer__nav-buttons';

        const backBtn = document.createElement('button');
        backBtn.type = 'button';
        backBtn.className = 'button-unstyled menu-drawer__back-button focus-inset';
        backBtn.setAttribute('on:click', 'header-drawer/back');
        backBtn.setAttribute('aria-label', `Back to ${title}`);

        const backCaret = listItem.closest('header-drawer')?.querySelector('.icon-caret--backward');
        if (backCaret) {
          backBtn.appendChild(backCaret.cloneNode(true));
        } else {
          const caret = document.createElement('span');
          caret.className = 'svg-wrapper icon-caret icon-caret--backward';
          backBtn.appendChild(caret);
        }

        const backLabel = document.createElement('span');
        backLabel.className = 'menu-drawer__menu-item-text wrap-text';
        backLabel.textContent = title;
        backBtn.appendChild(backLabel);

        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = 'button-unstyled menu-drawer__close-button focus-inset';
        closeBtn.setAttribute('on:click', 'header-drawer/close');
        closeBtn.setAttribute('aria-label', 'Close');
        const closeIcon = listItem.closest('header-drawer')?.querySelector('.header-drawer-icon--close');
        if (closeIcon) {
          closeBtn.appendChild(closeIcon.cloneNode(true));
        }

        navButtons.appendChild(backBtn);
        navButtons.appendChild(closeBtn);
        inner.appendChild(navButtons);

        const columns = /** @type {HTMLElement} */ (columnsSource.cloneNode(true));
        while (columns.firstChild) {
          inner.appendChild(columns.firstChild);
        }

        if (featuredTpl) {
          inner.appendChild(featuredTpl.content.cloneNode(true));
        }

        submenu.appendChild(inner);
        details.appendChild(summary);
        details.appendChild(submenu);
        replacement.appendChild(details);
        listItem.replaceWith(replacement);
      });
    });
  }

  function run() {
    document.querySelectorAll('[data-pj-mega-configs]').forEach((configsRoot) => {
      const headerMenu = configsRoot.closest('header-menu');
      const scope = headerMenu || configsRoot.parentElement || document;
      attachDesktop(scope, configsRoot);
    });

    const configsRoot = document.querySelector('[data-pj-mega-configs]');
    if (!configsRoot) return;

    document.querySelectorAll('header-drawer, .header__drawer').forEach((drawer) => {
      attachDrawer(drawer, configsRoot);
    });
  }

  function boot() {
    run();

    // Section hydration remorphs [data-hydration-key] nodes after idle and can
    // replace the mobile drawer — re-attach mega panels if Liquid attach missed.
    const reattach = () => run();
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => {
        reattach();
        setTimeout(reattach, 0);
        setTimeout(reattach, 400);
      });
    } else {
      setTimeout(reattach, 0);
      setTimeout(reattach, 400);
    }

    const headerSection = document.querySelector('.header-section');
    if (headerSection) {
      let reattachTimer = 0;
      const observer = new MutationObserver(() => {
        if (
          !headerSection.querySelector(
            'header-drawer .menu-drawer__menu > li[data-pj-menu-handle]:not([data-pj-mega-attached])'
          )
        ) {
          return;
        }
        window.clearTimeout(reattachTimer);
        reattachTimer = window.setTimeout(run, 50);
      });
      // childList only — attribute morph during section hydration must not re-enter run()
      observer.observe(headerSection, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', run);
  document.addEventListener('shopify:block:select', run);
})();
